import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Heading } from "./NKDaily";
import { AvatarIcon, CountryFlag, COUNTRY_NAME } from "@/lib/identity";
import { CaretLeft, Crown, Trophy, Lightning, Skull, Crosshair, EnvelopeSimple, Calendar, Prohibit, Pause, Trash, Star, Shield } from "@phosphor-icons/react";

/**
 * Admin-only read-only view of a player's profile.
 * Lives at /admin/players/:userId — never leaks into the public /profile page.
 * All sensitive admin actions (suspend/ban/delete) stay in this view.
 */
export default function PlayerProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "super_admin";
  const [player, setPlayer] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/players/${userId}`);
      setPlayer(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Player not found");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const action = async (path, label) => {
    if (!window.confirm(`${label} this player?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/players/${userId}/${path}`);
      toast.success(`${label} done`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const grantNkPlus = async (days) => {
    if (!window.confirm(`Grant NK+ for ${days} days?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/nk-plus`, { user_id: userId, days, active: true });
      toast.success(`NK+ granted for ${days} days`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const revokeNkPlus = async () => {
    if (!window.confirm("Revoke NK+ for this player?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/nk-plus`, { user_id: userId, days: 0, active: false });
      toast.success("NK+ revoked");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const backfillLadder = async () => {
    setBusy(true);
    try {
      // Step 1 — dry-run to compute the delta
      const { data: dry } = await api.post(
        `/admin/ladder/backfill-uncapped-ss?user_id=${userId}&dry_run=true`
      );
      const row = (dry.results || [])[0];
      if (!row || row.credited <= 0) {
        if (dry.skipped_already_backfilled > 0) {
          toast.info("Already backfilled — no further SS to credit.");
        } else {
          toast.info("Nothing to backfill — this player's history already matches the ledger.");
        }
        return;
      }
      const ok = window.confirm(
        `Backfill uncapped Ladder SS for this player?\n\n` +
        `  Theoretical (no cap): ${row.theoretical.toLocaleString()}\n` +
        `  Currently credited:   ${row.actual.toLocaleString()}\n` +
        `  Will add:             +${row.credited.toLocaleString()} SS\n\n` +
        `This credits their Season Score + Country War totals. Idempotent.`
      );
      if (!ok) return;
      // Step 2 — apply
      const { data: applied } = await api.post(
        `/admin/ladder/backfill-uncapped-ss?user_id=${userId}&dry_run=false`
      );
      const credited = applied.total_ss_credited || 0;
      toast.success(`Backfilled +${credited.toLocaleString()} SS`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally {
      setBusy(false);
    }
  };

  const adjustField = async (kind /* "ss" | "rating" */) => {
    const label = kind === "ss" ? "Season Score" : "Knowledge Rating";
    const raw = window.prompt(
      `Adjust ${label} for this player.\n\n` +
      `Enter a SIGNED amount (e.g. 1000 to add, -500 to subtract).`,
      ""
    );
    if (raw === null) return;
    const delta = parseInt(String(raw).replace(/[^\d-]/g, ""), 10);
    if (!Number.isFinite(delta) || delta === 0) {
      toast.error("Enter a non-zero number");
      return;
    }
    const reason = window.prompt(
      `Reason (audit log) — required.\n` +
      `e.g. "compensation for outage", "manual correction", "anti-fraud clawback"`,
      ""
    );
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error("A reason is required");
      return;
    }
    setBusy(true);
    try {
      const path = kind === "ss" ? "adjust-ss" : "adjust-rating";
      const { data } = await api.post(
        `/admin/players/${userId}/${path}`,
        { delta, reason: reason.trim() }
      );
      if (kind === "ss") {
        toast.success(`SS adjusted by ${delta > 0 ? "+" : ""}${delta}. New: ${data.new_season_score.toLocaleString()}`);
      } else {
        toast.success(`Rating adjusted by ${delta > 0 ? "+" : ""}${delta}. New: ${data.new_rating}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Adjustment failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Permanently DELETE this player? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/players/${userId}`);
      toast.success("Player deleted");
      navigate("/admin/players");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!player) return <p className="text-sm text-nk-mute">Loading…</p>;

  const u = player.user || player;
  const s = player.stats || {};

  return (
    <div className="space-y-6" data-testid="admin-player-profile">
      <Link to="/admin/players" data-testid="player-back"
            className="inline-flex items-center gap-1 text-xs text-nk-mute hover:text-nk-text">
        <CaretLeft size={12} /> Back to players
      </Link>

      <Heading title={u.username || u.name || u.email} subtitle="Read-only player profile · admin view" />

      <div className="nk-card p-5 sm:p-6 flex flex-wrap items-start gap-5">
        <AvatarIcon id={u.avatar} size={56} />
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-2xl font-medium">{u.username || u.name || "—"}</h2>
            <CountryFlag country={u.country} size={14} />
            <span className="text-xs text-nk-mute">{COUNTRY_NAME[u.country] || u.country}</span>
            {u.nk_plus_active && <span className="text-[10px] bg-nk-gold/20 text-nk-gold px-1.5 py-0.5 rounded-sm uppercase">NK+</span>}
            {u.suspended && <span className="text-[10px] bg-nk-warn/20 text-nk-warn px-1.5 py-0.5 rounded-sm uppercase">Suspended</span>}
            {u.banned && <span className="text-[10px] bg-nk-bad/20 text-nk-bad px-1.5 py-0.5 rounded-sm uppercase">Banned</span>}
          </div>
          <p className="text-[11px] text-nk-dim font-mono flex items-center gap-1.5">
            <EnvelopeSimple size={10} /> {u.email}
          </p>
          <p className="text-[11px] text-nk-dim font-mono flex items-center gap-1.5">
            <Calendar size={10} /> joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
            · last seen {u.last_seen_at ? new Date(u.last_seen_at).toLocaleString() : "—"}
          </p>
          <p className="text-[10px] text-nk-dim font-mono">id: {u.user_id} · role: {u.role || "player"}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Rating" value={s.knowledge_rating ?? "—"} sub={`peak ${s.rating_peak ?? "—"}`} />
        <Stat label="Season Score" value={(s.season_score ?? 0).toLocaleString()} sub="this season" accent />
        <Stat label="Survival best" value={s.survival_record ?? 0} sub={`${s.survival_runs ?? 0} runs`} />
        <Stat label="Daily entries" value={s.daily_entries ?? 0} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <ModeStat Icon={Lightning} label="Daily wins" v={s.daily_wins ?? 0} />
        <ModeStat Icon={Trophy} label="Ladder appearances" v={s.ladder_appearances ?? 0} />
        <ModeStat Icon={Crosshair} label="Duel record" v={`${s.duel_wins ?? 0}–${s.duel_losses ?? 0}`} />
        <ModeStat Icon={Crown} label="Championships" v={s.championships ?? 0} />
      </div>

      {/* NK+ subscription */}
      <div className="nk-card p-5 border-l-2 border-nk-gold/40" data-testid="admin-nk-plus-panel">
        <div className="flex items-center gap-2 mb-3">
          <Star size={14} weight="fill" className="text-nk-gold" />
          <p className="eyebrow text-nk-gold">NK+ subscription</p>
        </div>
        {u.nk_plus_active ? (
          <div className="space-y-3">
            <p className="text-xs text-nk-text">
              Active
              {u.nk_plus_expires_at && (
                <span className="text-nk-mute"> · expires {new Date(u.nk_plus_expires_at).toLocaleDateString()}</span>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {[7, 30, 90, 365].map((d) => (
                <button key={d} onClick={() => grantNkPlus(d)} disabled={busy}
                        data-testid={`nkplus-extend-${d}`}
                        className="text-xs px-3 py-1.5 border border-nk-gold/60 text-nk-gold hover:bg-nk-gold/10 rounded-sm">
                  +{d}d
                </button>
              ))}
              <button onClick={revokeNkPlus} disabled={busy}
                      data-testid="nkplus-revoke-btn"
                      className="ml-auto text-xs px-3 py-1.5 border border-nk-bad/60 text-nk-bad hover:bg-nk-bad/10 rounded-sm">
                Revoke NK+
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-nk-mute">Not active. Grant a subscription:</p>
            <div className="flex flex-wrap gap-2">
              {[7, 30, 90, 365].map((d) => (
                <button key={d} onClick={() => grantNkPlus(d)} disabled={busy}
                        data-testid={`nkplus-grant-${d}`}
                        className="text-xs px-3 py-1.5 border border-nk-gold/60 text-nk-gold hover:bg-nk-gold/10 rounded-sm">
                  Grant {d}d
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Super-admin only: manual SS + Rating adjustments */}
      {isSuperAdmin && (
        <div className="nk-card p-5 border-l-2 border-nk-gold" data-testid="superadmin-adjust-panel">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={14} weight="fill" className="text-nk-gold" />
            <p className="eyebrow text-nk-gold">Super-admin · manual adjust</p>
          </div>
          <p className="text-xs text-nk-mute mb-3">
            Directly add or subtract Season Score and Knowledge Rating. Both signed
            (use negative to claw back). Every change is logged with a reason.
          </p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => adjustField("ss")} disabled={busy}
                    data-testid="player-adjust-ss-btn"
                    className="text-xs px-3 py-1.5 border border-nk-gold/60 text-nk-gold hover:bg-nk-gold/10 rounded-sm">
              Adjust Season Score…
            </button>
            <button onClick={() => adjustField("rating")} disabled={busy}
                    data-testid="player-adjust-rating-btn"
                    className="text-xs px-3 py-1.5 border border-nk-gold/60 text-nk-gold hover:bg-nk-gold/10 rounded-sm">
              Adjust Rating…
            </button>
          </div>
        </div>
      )}

      {/* Admin actions */}
      <div className="nk-card p-5 border-l-2 border-nk-warn/40">
        <p className="eyebrow text-nk-warn mb-3">Admin actions</p>
        <div className="flex flex-wrap gap-2">
          {!u.suspended ? (
            <button onClick={() => action("suspend", "Suspend")} disabled={busy}
                    data-testid="player-suspend-btn"
                    className="text-xs px-3 py-1.5 border border-nk-warn/60 text-nk-warn hover:bg-nk-warn/10 rounded-sm inline-flex items-center gap-1.5">
              <Pause size={11} weight="bold" /> Suspend
            </button>
          ) : (
            <button onClick={() => action("unsuspend", "Unsuspend")} disabled={busy}
                    data-testid="player-unsuspend-btn"
                    className="text-xs px-3 py-1.5 border border-nk-accent/60 text-nk-accent hover:bg-nk-accent/10 rounded-sm">
              Unsuspend
            </button>
          )}
          {!u.banned ? (
            <button onClick={() => action("ban", "Ban")} disabled={busy}
                    data-testid="player-ban-btn"
                    className="text-xs px-3 py-1.5 border border-nk-bad/60 text-nk-bad hover:bg-nk-bad/10 rounded-sm inline-flex items-center gap-1.5">
              <Prohibit size={11} weight="bold" /> Ban
            </button>
          ) : (
            <button onClick={() => action("unban", "Unban")} disabled={busy}
                    data-testid="player-unban-btn"
                    className="text-xs px-3 py-1.5 border border-nk-accent/60 text-nk-accent hover:bg-nk-accent/10 rounded-sm">
              Unban
            </button>
          )}
          <button onClick={() => action("reset-stats", "Reset stats for")} disabled={busy}
                  className="text-xs px-3 py-1.5 border border-nk-line text-nk-mute hover:text-nk-text rounded-sm">
            Reset stats
          </button>
          <button onClick={() => action("recompute-history", "Recompute SS history for")} disabled={busy}
                  data-testid="player-recompute-history-btn"
                  className="text-xs px-3 py-1.5 border border-nk-gold/60 text-nk-gold hover:bg-nk-gold/10 rounded-sm">
            Recompute SS history
          </button>
          <button onClick={backfillLadder} disabled={busy}
                  data-testid="player-backfill-ladder-btn"
                  title="Credit this player the Ladder SS lost to the (removed) 5,000/day cap. Idempotent."
                  className="text-xs px-3 py-1.5 border border-nk-gold/60 text-nk-gold hover:bg-nk-gold/10 rounded-sm">
            Backfill uncapped Ladder SS
          </button>
          <button onClick={remove} disabled={busy}
                  data-testid="player-delete-btn"
                  className="ml-auto text-xs px-3 py-1.5 bg-nk-bad/20 border border-nk-bad/60 text-nk-bad hover:bg-nk-bad/30 rounded-sm inline-flex items-center gap-1.5">
            <Trash size={11} weight="bold" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div className="nk-card p-4">
      <p className="text-[10px] uppercase tracking-wider text-nk-dim">{label}</p>
      <p className={`font-display text-2xl mt-1 ${accent ? "text-nk-accent" : ""}`}>{value}</p>
      {sub && <p className="text-[10px] text-nk-dim mt-0.5 font-mono">{sub}</p>}
    </div>
  );
}

function ModeStat({ Icon, label, v }) {
  return (
    <div className="nk-card p-3 flex items-center gap-3">
      <Icon size={16} weight="duotone" className="text-nk-accent shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-nk-dim">{label}</p>
        <p className="text-sm font-medium">{v}</p>
      </div>
    </div>
  );
}

ModeStat.defaultProps = { Icon: Lightning };
Stat.defaultProps = { accent: false, sub: "" };

void Skull; // keep import for future stat addition
